import { useCallback, useEffect, useState } from "react";

import type { BsPaginated } from "services/billing";

/**
 * [PRD-2043] Drives a billing-service cursor-paginated list.
 *
 * `load(cursor)` must return billing-service's `{ data, cursor, hasMore }`
 * envelope. Pass a `useCallback`-memoized `load` so the initial fetch doesn't
 * loop. "Load more" appends the next page using the returned cursor.
 */
export function useCursorList<T>(load: (cursor?: string) => Promise<BsPaginated<T>>) {
  const [rows, setRows] = useState<T[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = useCallback(
    async (nextCursor: string | undefined, append: boolean) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      try {
        const res = await load(nextCursor);
        const data = res?.data ?? [];
        setRows((prev) => (append ? [...prev, ...data] : data));
        setCursor(res?.cursor ?? null);
        setHasMore(Boolean(res?.hasMore));
        setError(null);
      } catch {
        setError("Failed to load data.");
        if (!append) setRows([]);
      } finally {
        if (append) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [load]
  );

  useEffect(() => {
    fetchPage(undefined, false);
  }, [fetchPage]);

  const loadMore = () => {
    if (cursor) fetchPage(cursor, true);
  };

  const reload = () => fetchPage(undefined, false);

  return { rows, hasMore, loading, loadingMore, error, loadMore, reload };
}
